<i18n>
{
	"en": {
		"title": "Change stretch",
		"fromTripStart": "From Trip Start",
		"fromPickupLocation": "From Pickup Location",
		"toTripEnd": "To Trip End",
		"toDeliveryLocation": "To Delivery Location",
		"moveOrder": "Move Order to Trip",
		"multipleLocations": "Multiple Locations",
		"plannedETD": "Planned Departure",
		"plannedETA": "Planned Arrival",
		"selectDate": "Select date",
		"outOfRangeWarning": "Selected date is outside the trip planned date range.",
		"customFromLocation": "Custom Pickup Location",
		"customToLocation": "Custom Unloading Location",
		"useCustomFromLocation": "Use custom pickup location",
		"useCustomToLocation": "Use custom unloading location",
		"tripNotSelected": "No trip selected",
		"fromViaLocation": "From Via Location",
		"toViaLocation": "To Via Location"
	},
	"no": {
		"title": "Endre strekninger",
		"fromTripStart": "Fra turstart",
		"fromPickupLocation": "Fra hentested",
		"toTripEnd": "Til turslutt",
		"toDeliveryLocation": "Til leveringssted",
		"moveOrder": "Legg til på tur",
		"multipleLocations": "Flere steder",
		"plannedETD": "Planlagt Avgang",
		"plannedETA": "Planlagt Ankomst",
		"selectDate": "Velg dato",
		"outOfRangeWarning": "Selected date is outside the trip planned date range.",
		"customFromLocation": "Egendefinert hentested",
		"customToLocation": "Egendefinert leveringssted",
		"useCustomFromLocation": "Bruk egendefinert hentested",
		"useCustomToLocation": "Bruk egendefinert leveringssted",
		"tripNotSelected": "No trip selected",
		"fromViaLocation": "Fra Via Location",
		"toViaLocation": "Til Via Location"
	}
}
</i18n>
<template>
	<t-dialog
		:isShown="isShown"
		:title="$t('title')"
		icon="order"
		iconColor="blue"
		width="600px"
		@close="$emit('close')"
		@shown="processShown"
	>
		<div class="dialog-top">
			<TripsSearchInput
				ref="tripSelect"
				:selectedTrip="newTrip"
				width="100%"
				@change="onTripSelected"
			/>
		</div>
		<div class="dialog-mid">
			<div class="dialog-left">
				<t-form-group :title="$t('plannedETD')">
					<div style="display:flex; margin-bottom:10px">
						<t-date-picker
							:placeholder="$t('selectDate')"
							:value="displayDateFormatter(etd.date)"
							:dateFormat="displayDateFormat"
							style="margin-right: 5px;"
							@input="chandEtd({dateValue: $event, hasTime: null})"
						/>
						<t-time-picker
							:value="etdTime"
							@input="chandEtd({timeValue: $event, hasTime: Boolean($event)})"
						/>
					</div>
				</t-form-group>
				<t-form-group :title="$t('plannedETA')">
					<div style="display:flex">
						<t-date-picker
							:placeholder="$t('selectDate')"
							:value="displayDateFormatter(eta.date)"
							:dateFormat="displayDateFormat"
							style="margin-right: 5px;"
							@input="changeEta({dateValue: $event, hasTime: null})"
						/>

						<t-time-picker
							:value="etaTime"
							@input="changeEta({timeValue: $event, hasTime: Boolean($event)})"
						/>
					</div>
				</t-form-group>
				<div class="custom-switches">
					<t-switch
						v-model="allowCustomFrom"
						:label="$t('useCustomFromLocation')"
						@change="customFromToggle"
					/>
					<t-switch
						v-model="allowCustomTo"
						:label="$t('useCustomToLocation')"
						@change="customToToggle"
					/>
				</div>
			</div>
			<div class="dialog-right">
				<t-stretch-select
					v-if="fromLocations && toLocations"
					:fromLocations="fromLocations"
					:fromIndex="from"
					:toLocations="toLocations"
					:toIndex="to"
					:allowCustomFrom="allowCustomFrom"
					:allowCustomTo="allowCustomTo"
					@changeFromLocation="changeFromLocation"
					@changeToLocation="changeToLocation"
				>
					<template v-slot:customFromDetails="{location, index}">
						<t-select
							:items="location.items"
							:selectedItem="location.selectedItem"
							width="100%"
							displayName="name"
							relative
							@change="onChangeFromViaPlace(index, $event)"
						>
							<template #optionItemValue="slotProps">
								{{slotProps.item.name}}
							</template>
						</t-select>
					</template>
					<template v-slot:customToDetails="{location, index}">
						<t-select
							:items="location.items"
							:selectedItem="location.selectedItem"
							width="100%"
							displayName="name"
							relative
							@change="onChangeToViaPlace(index, $event)"
						>
							<template #optionItemValue="slotProps">
								{{slotProps.item.name}}
							</template>
						</t-select>
					</template>
					<template v-slot:customFrom>
						<GlobalAddressInput
							:title="$t('customFromLocation')"
							:value="customFromLocation"
							@change="onCustomFromSelected"
						/>
					</template>
					<template v-slot:customTo>
						<GlobalAddressInput
							:title="$t('customToLocation')"
							:value="customToLocation"
							@change="onCustomToSelected"
						/>
					</template>
				</t-stretch-select>
			</div>
		</div>
		<template v-slot:footer>
			<t-button
				:disabled="disableSave"
				:busy="disableSubmit"
				appearance="primary"
				class="timpex-modal-button"
				width="100%"
				@click="moveOrder"
			>
				{{$t('moveOrder')}}
			</t-button>
		</template>
	</t-dialog>
</template>
<script>
import {
	displayDateFormat, storageDateFormat, createDateObject, displayDateFormatter
} from '@/core';
import TripsSearchInput from '@/modules/trips/TripsSearchInput';
import moment from 'moment';
import deepCopy from 'deepcopy';
import TStretchSelect from '@/components/evergreen/timpex/inputs/TStretchSelect';
import GlobalAddressInput from '@/modules/globalAddresses/GlobalAddressInput';

export default {
	name: 'MoveOrderToDifferentTripSelect',
	components: {
		GlobalAddressInput,
		TStretchSelect,
		TripsSearchInput
	},
	props: {
		isShown: Boolean,
		currentTrip: Object,
		order: Object,
		disableSubmit: Boolean,
	},
	data() {
		return {
			newTrip: null,
			fromLocations: [],
			toLocations: [],
			customFromLocation: {},
			customToLocation: {},
			from: 0,
			to: 0,
			etd: {
				date: '',
				hasTime: false,
				timeZone: 'Europe/Oslo',
				epoch: ''
			},
			eta: {
				date: '',
				hasTime: false,
				timeZone: 'Europe/Oslo',
				epoch: ''
			},
			allowCustomFrom: false,
			allowCustomTo: false,
			defaultStartIndex: 0,
			defaultEndIndex: 0,
		};
	},
	computed: {
		displayDateFormat() {
			return displayDateFormat;
		},
		etdOutOfRange() {
			if (!this.selectedTrip || !this.selectedTrip.etd) return true;
			return this.etd.date < this.selectedTrip.etd.date || (this.selectedTrip.eta && this.etd.date > this.selectedTrip.eta.date);
		},
		etaOutOfRange() {
			if (!this.selectedTrip || !this.selectedTrip.eta) return true;
			return (this.selectedTrip.etd && this.eta.date < this.selectedTrip.etd.date) || this.eta.date > this.selectedTrip.eta.date;
		},
		selectedTrip() {
			return this.newTrip || this.currentTrip;
		},
		tripNumber() {
			return this.currentTrip ? this.currentTrip.tripNumber : '';
		},
		orderNumber() {
			if (!this.order) return '';
			return this.order.orderNumber;
		},
		newTripFromAddress() {
			return this.newTrip && this.newTrip.fromAddress ? this.newTrip.fromAddress.name : this.$t('tripNotSelected');
		},
		newTripToAddress() {
			return this.newTrip && this.newTrip.toAddress ? this.newTrip.toAddress.name : this.$t('tripNotSelected');
		},
		disableSave() {
			return !this.newTrip || !this.newTrip.tripNumber;
		},
		etdTime() {
			if (!this.etd.hasTime) return null;

			return this.etd.date ?
				moment(this.etd.date, storageDateFormat).format('LT') :
				undefined;
		},
		etaTime() {
			if (!this.eta.hasTime) return null;

			return this.eta.date ?
				moment(this.eta.date, storageDateFormat).format('LT') :
				undefined;
		},
	},
	methods: {
		displayDateFormatter,
		processShown() {
			this.fromLocations = [];
			this.toLocations = [];
			if (this.selectedTrip.fromAddress) {
				this.fromLocations.push({
					label: this.$t('fromTripStart'),
					labelClass: 'stretch-details-label',
					primaryClass: 'stretch-details-primary',
					name: this.selectedTrip.fromAddress.name,
					option: 'trip'
				});
				this.defaultStartIndex = 0;
			}
			if (this.selectedTrip.toAddress) {
				this.toLocations.push({
					label: this.$t('toTripEnd'),
					labelClass: 'stretch-details-label',
					primaryClass: 'stretch-details-primary',
					name: this.selectedTrip.toAddress.name,
					option: 'trip'
				});
				this.defaultEndIndex = 0;
			}

			if (this.selectedTrip.viaLocations && this.selectedTrip.viaLocations.length > 1) {
				this.fromLocations.push({
					label: this.$t('fromViaLocation'),
					labelClass: 'stretch-details-label',
					option: 'via',
					select: true,
					items: this.selectedTrip.viaLocations,
					selectedItem: this.selectedTrip.viaLocations[0]
				});
				this.toLocations.push({
					label: this.$t('toViaLocation'),
					labelClass: 'stretch-details-label',
					option: 'via',
					select: true,
					items: this.selectedTrip.viaLocations,
					selectedItem: this.selectedTrip.viaLocations[0]
				});
			} else if (this.selectedTrip.viaLocations && this.selectedTrip.viaLocations.length === 1) {
				this.fromLocations.push({
					label: this.$t('fromViaLocation'),
					labelClass: 'stretch-details-label',
					primaryClass: 'stretch-details-primary',
					name: this.selectedTrip.viaLocations[0].name,
					selectedItem: this.selectedTrip.viaLocations[0],
					option: 'via'
				});
				this.toLocations.push({
					label: this.$t('toViaLocation'),
					labelClass: 'stretch-details-label',
					primaryClass: 'stretch-details-primary',
					name: this.selectedTrip.viaLocations[0].name,
					selectedItem: this.selectedTrip.viaLocations[0],
					option: 'via'
				});
			}

			if (this.order) {
				let despatchParty = this.order.despatchParty || this.order.consignor;
				if (despatchParty) {
					if (this.order.despatchOption === 'despatchParty') {
						let fromName = despatchParty.name;
						this.fromLocations.unshift({
							label: this.$t('fromPickupLocation'),
							labelClass: 'stretch-details-label',
							primaryClass: 'stretch-details-primary',
							name: fromName,
							option: 'despatchParty'
						});
						this.onCustomFromSelected({});
						this.allowCustomFrom = false;
						if (this.selectedTrip.type && this.selectedTrip.type.internalCode && this.selectedTrip.type.internalCode.toUpperCase() === 'L'
							|| (this.order.next && this.order.next.length === 0)
						) {
							this.defaultStartIndex = 0;
						} else {
							this.defaultStartIndex = 1;
						}
					} else {
						this.onCustomFromSelected(despatchParty);
						this.defaultStartIndex = this.fromLocations.length;
						this.allowCustomFrom = true;
					}
				}

				let deliveryParty = this.order.deliveryParty || this.order.consignee;
				if (deliveryParty) {
					if (this.order.deliveryOption === 'deliveryParty') {
						let toName = deliveryParty.name;
						this.toLocations.push({
							label: this.$t('toDeliveryLocation'),
							labelClass: 'stretch-details-label',
							primaryClass: 'stretch-details-primary',
							name: toName,
							option: 'deliveryParty'
						});
						this.onCustomToSelected({});
						this.allowCustomTo = false;
						this.defaultEndIndex = this.toLocations.length - 1;
					} else {
						this.onCustomToSelected(deliveryParty);
						this.defaultEndIndex = this.toLocations.length;
						this.allowCustomTo = true;
					}
				}
			}
			this.etd = deepCopy(this.order.etd);
			this.eta = deepCopy(this.order.eta);
			this.changeFromLocation(this.defaultStartIndex);
			this.changeToLocation(this.defaultEndIndex);
		},
		onTripSelected(trip) {
			if (!trip) return;
			if (trip) {
				this.newTrip = trip;
				this.newTrip.legs = trip.legs.map(x => x);
				this.newTrip.orders = trip.orders.map(x => x);
			} else {
				this.newTrip = trip;
			}
			this.processShown();
		},
		moveOrder() {
			let despatchOption = this.from >= this.fromLocations.length ? 'custom' : this.fromLocations[this.from].option;
			let fromLocation = this.customFromLocation;
			if (despatchOption === 'via') {
				despatchOption = 'custom';
				fromLocation = this.fromLocations[this.from].selectedItem;
			}

			let deliveryOption = this.to >= this.toLocations.length ? 'custom' : this.toLocations[this.to].option;
			let toLocation = this.customToLocation;
			if (deliveryOption === 'via')
				toLocation = this.toLocations[this.to].selectedItem;

			this.$emit('moveOrder',{
				subOrder: this.order,
				originalTrip: this.currentTrip,
				newTrip: this.newTrip,
				despatchOption: despatchOption,
				deliveryOption: deliveryOption,
				etd: this.etd,
				eta: this.eta,
				customDespatchParty: fromLocation,
				customDeliveryParty: toLocation
			});
		},
		changeFromLocation(index) {
			this.from = index;
		},
		changeToLocation(index) {
			this.to = index;
		},
		onCustomFromSelected(value) {
			this.customFromLocation = value;
		},
		onCustomToSelected(value) {
			this.customToLocation = value;
		},
		customFromToggle(value) {
			if (value) {
				this.changeFromLocation(this.fromLocations.length);
			} else {
				this.changeFromLocation(this.defaultStartIndex);
			}
		},
		customToToggle(value) {
			if (value) {
				this.changeToLocation(this.toLocations.length);
			} else {
				this.changeToLocation(this.defaultEndIndex);
			}
		},
		onChangeFromViaPlace(fromIndex, value) {
			if (this.fromLocations[fromIndex]) {
				this.fromLocations[fromIndex].selectedItem = value;
				this.from = fromIndex;
			}
		},
		onChangeToViaPlace(toIndex, value) {
			if (this.toLocations[toIndex]) {
				this.toLocations[toIndex].selectedItem = value;
				this.to = toIndex;
			}
		},
		chandEtd({ dateValue, timeValue, hasTime }) {
			let storedDate = this.etd ? this.etd.date : undefined;
			let date = dateValue ? dateValue : storedDate;

			let time = timeValue ?
				moment(timeValue, 'LT').format('HH:mm') :
				moment(storedDate, storageDateFormat).format('HH:mm');

			hasTime = hasTime === null ?
				this.etd.hasTime :
				hasTime;

			this.etd = createDateObject({ date, hasTime, time }, Boolean(dateValue));
		},
		chandEta({ dateValue, timeValue, hasTime }) {
			let storedDate = this.eta ? this.eta.date : undefined;
			let date = dateValue ? dateValue : storedDate;

			let time = timeValue ?
				moment(timeValue, 'LT').format('HH:mm') :
				moment(storedDate, storageDateFormat).format('HH:mm');

			hasTime = hasTime === null ?
				this.eta.hasTime :
				hasTime;

			this.eta = createDateObject({ date, hasTime, time }, Boolean(dateValue));
		}
	},
};
</script>

<style >
.stretch-details-label {
	font-size: 11px;
	font-weight: 500;
}
.stretch-details-primary {
	font-size: 12px;
}
</style>

<style lang="scss" scoped>
.dialog-mid {
	display: grid;
	column-gap: 20px;
	grid-template-columns: 225px auto;
	margin-top:15px;
	padding-bottom: 20px;
	.custom-switches {
		display: grid;
		grid-row-gap: 10px;
		grid-template-rows: repeat(2, min-content);
		padding-top:20px;
	}
}
</style>
